import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { plural } from "../plural";
import { useRebaseClient, useSnackbarController } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import {
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    Separator as RebaseSeparator,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import {
    Globe as RebaseGlobeIcon,
    Link2 as RebaseLinkIcon,
    MapPin as RebaseMapPinIcon
} from "lucide-react";

import { ChipList, Fact, Notes, RecentThreads, coverRootCls } from "./_cover";
import { CompanyClaimsSection } from "./CompanyClaimsSection";
import { CompanyPresentations } from "./PresentCandidates";
import { EmailComposer, type ComposerTarget } from "./EmailComposer";
import { callFunction, type MailboxStatus } from "./_email_shared";
import { EntityLink } from "./_links";
import {
    DUE_COLOR,
    STATUS_COLOR,
    dueLabel,
    dueState,
    prospectsUrl,
    sinceLabel,
    statusLabel,
    type ProspectStatus
} from "./_prospects_shared";
import { CompanyInvitationsPanel } from "./CompanyInvitationsPanel";

interface MemberRow {
    id: string;
    contact_name?: string | null;
    contact_position?: string | null;
    contact_email?: string | null;
    role?: string | null;
    created_at?: string | null;
}

interface LeadRow {
    id: string;
    requested_services?: string[] | null;
    needs_description?: string | null;
    employer_key?: string | null;
    lead_status?: string | null;
    created_at?: string | null;
}

/** The ids `company_leads.requested_services` declares, in the tone of a sentence. */
const LEAD_SERVICE_LABEL: Record<string, string> = {
    headhunting: "selección y contratación",
    attraction_optimization: "atracción y optimización de procesos"
};

/**
 * What each person's recorded role is, in words — never what it lets them do.
 *
 * Nothing reads `company_members.role`, so every member can do everything
 * (drizzle/0070). These labels used to describe four levels of permission —
 * "administra la cuenta", "sólo puede mirar" — that were never built, and a
 * recruiter reading them would have believed a "solo lectura" member could not
 * publish. `owner` is written by registration; `member` by an invitation and
 * as the panel's default, so it says only "miembro"; the other three were
 * never written by the product and say only that somebody set them.
 */
const MEMBER_ROLE_LABEL: Record<string, string> = {
    owner: "registró la empresa",
    member: "miembro",
    admin: "marcado como administrador (no cambia lo que puede hacer)",
    recruiter: "marcado como reclutador (no cambia lo que puede hacer)",
    viewer: "marcado como solo lectura (no cambia lo que puede hacer)"
};

const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const GlobeIcon = RebaseGlobeIcon as React.ComponentType<any>;
const LinkIcon = RebaseLinkIcon as React.ComponentType<any>;
const MapPinIcon = RebaseMapPinIcon as React.ComponentType<any>;

/**
 * A company's front cover.
 *
 * `companies` is the employer directory behind the public board — logos, brand
 * colours, the description a visitor reads — and its record opened on a form of
 * forty fields, half of them brand data nobody edits by hand.
 *
 * What a person actually asks while looking at an employer is three questions,
 * and until now each was on a different screen or on none:
 *
 *  - **are they hiring with us** → the vacancies pointing at this company;
 *  - **are we working them** → their prospect row in "Empresas que contratan",
 *    which is filed under `employer_key` and was invisible from here;
 *  - **have we spoken** → the mailbox, filed under this record *or* under that
 *    same prospect key. 0044 put `employer_key` on `companies` precisely so one
 *    business stops having two disjoint histories; this reads both.
 *
 * Read-only, and deliberately so, with two exceptions. Prospecting is worked in
 * "Empresas que contratan", where the next step, the contacts and the history
 * live; a second set of those controls here would be a worse copy of that
 * screen, so this says what is true and links to where the work happens — at
 * this employer, not at the top of that screen's queue.
 *
 * The first exception is writing to them. "Escribir a …" used to leave for Correo's
 * blank composer, which lost the record and filed nothing against it; it opens
 * here now, the way the candidate's cover does it, and what it sends is filed
 * against this company.
 *
 * The second is presenting candidates (drizzle/0069), because it is done to a
 * company with an account, and this is the record of that company: who we
 * presented, what they did with each one, and the button to present somebody
 * else. See `PresentCandidates.tsx`.
 */

/** Who the cover's composer is writing to, and which button opened it. */
interface ComposeTarget {
    email: string;
    name: string | null;
    subject: string;
    tone: "respuesta" | "seguimiento";
    /** "next" for the card at the top, or a member's id — the composer opens where it was asked for. */
    origin: string;
}

const SIZE_LABEL: Record<string, string> = {
    "1-10": "1–10 personas",
    "11-50": "11–50 personas",
    "51-200": "51–200 personas",
    "201-500": "201–500 personas",
    "501-1000": "501–1000 personas",
    "1000+": "más de 1000 personas"
};

const JOB_STATUS_LABEL: Record<string, string> = {
    pending_review: "Pendiente de revisión",
    published: "Publicada",
    returned: "Devuelta con nota",
    draft: "Borrador",
    closed: "Cerrada"
};

const JOB_STATUS_COLOR: Record<string, string> = {
    pending_review: "orangeLighter",
    published: "greenLighter",
    returned: "purpleLighter",
    draft: "grayLighter",
    closed: "redLighter"
};

/** How many vacancies the cover names before it stops being a summary. */
const JOBS_SHOWN = 5;

interface JobRow {
    id: string;
    title?: string | null;
    status?: string | null;
    created_at?: string | null;
}

interface ProspectRow {
    id: string;
    status?: string | null;
    last_contacted_at?: string | null;
    next_action?: string | null;
    next_action_at?: string | null;
    origin?: string | null;
}

export function CompanyOverview({ entity, modifiedValues }: EntityCustomViewParams) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;

    const companyId = entity?.id ? String(entity.id) : null;
    const values = (modifiedValues ?? entity?.values ?? {}) as Record<string, any>;
    const employerKey = (values.employer_key ?? "").toString().trim() || null;

    const [jobs, setJobs] = useState<JobRow[] | null>(null);
    // Separate from `jobs === null`, which is also the state before the first
    // answer arrives: "still reading" and "could not read" are different
    // sentences, and printing the second while the first is true reads as a
    // broken screen.
    const [jobsFailed, setJobsFailed] = useState(false);
    const [prospect, setProspect] = useState<ProspectRow | null>(null);
    const [contacts, setContacts] = useState<number | null>(null);
    /**
     * The people with an account at this company, and what they asked for
     * when they registered. Both were tabs ("Miembros", "Registro comercial")
     * of raw rows; here they are what they are — who to write to, and why
     * they came.
     */
    const [members, setMembers] = useState<MemberRow[]>([]);
    const [leads, setLeads] = useState<LeadRow[]>([]);
    const [applicationsReceived, setApplicationsReceived] = useState<number | null>(null);

    /**
     * Which company the last request was for. Same guard as the other covers:
     * a tab panel is keyed by the view rather than by the record, so a late
     * answer would file one employer's vacancies and outreach under another's.
     */
    const wanted = useRef<string | null>(null);

    useEffect(() => {
        setJobs(null);
        setJobsFailed(false);
        setProspect(null);
        setContacts(null);
        setMembers([]);
        setLeads([]);
        setApplicationsReceived(null);
    }, [companyId]);

    const load = useCallback(async () => {
        if (!data || !companyId) return;
        wanted.current = companyId;

        // Their vacancies. The relation tab lists them too; this is the count
        // and the three most recent, which is what "are they hiring" needs.
        try {
            const page = await data.collection("jobs").find({
                where: { company_id: ["==", companyId] },
                orderBy: ["created_at", "desc"],
                limit: 50
            });
            if (wanted.current !== companyId) return;
            setJobs((page?.data ?? []) as JobRow[]);
            setJobsFailed(false);
        } catch {
            if (wanted.current !== companyId) return;
            setJobs(null);
            setJobsFailed(true);
        }

        // Who is behind the record, and why they came. Three small reads
        // that used to be three tabs. Each fails on its own: a cover that
        // cannot say how many applications came in still says who to write to.
        const [memberPage, leadPage, applicationPage] = await Promise.all([
            data.collection("company-members").find({
                where: { company_id: ["==", companyId] }, orderBy: ["created_at", "asc"], limit: 50
            }).catch(() => undefined),
            data.collection("company-leads").find({
                where: { company_id: ["==", companyId] }, orderBy: ["created_at", "desc"], limit: 20
            }).catch(() => undefined),
            data.collection("talent-applications").find({
                where: { company_id: ["==", companyId] }, limit: 1
            }).catch(() => undefined)
        ]);
        if (wanted.current !== companyId) return;
        setMembers((memberPage?.data ?? []) as MemberRow[]);
        setLeads((leadPage?.data ?? []) as LeadRow[]);
        setApplicationsReceived(applicationPage ? (applicationPage.meta?.total ?? (applicationPage.data ?? []).length) : null);

        // Their prospect row, if the same employer is being worked. Keyed by
        // `employer_key` — the name-derived key the outreach side files under —
        // which is the whole point of 0044.
        if (!employerKey) {
            setProspect(null);
            setContacts(null);
            return;
        }
        try {
            const [prospects, people] = await Promise.all([
                data.collection("company-prospects").find({
                    where: { employer_key: ["==", employerKey] },
                    limit: 1
                }),
                data.collection("prospect-contacts").find({
                    where: { employer_key: ["==", employerKey] },
                    limit: 100
                })
            ]);
            if (wanted.current !== companyId) return;
            setProspect(((prospects?.data ?? []) as ProspectRow[])[0] ?? null);
            setContacts(((people?.data ?? []) as unknown[]).length);
        } catch {
            if (wanted.current !== companyId) return;
            setProspect(null);
            setContacts(null);
        }
    }, [data, companyId, employerKey]);

    useEffect(() => { void load(); }, [load]);

    const snackbar = useSnackbarController();
    const [compose, setCompose] = useState<ComposeTarget | null>(null);
    /** Whether the mailbox can send. Asked the first time somebody writes, not on every cover. */
    const [mailbox, setMailbox] = useState<MailboxStatus | null | undefined>(undefined);
    /** Bumped after a send, so "Correo" below lists what was just sent. */
    const [sent, setSent] = useState(0);

    useEffect(() => { setCompose(null); }, [companyId]);

    useEffect(() => {
        if (!compose || mailbox !== undefined) return;
        callFunction<MailboxStatus>(client, "sync-inbox")
            .then(setMailbox)
            .catch(() => setMailbox(null));
    }, [client, compose, mailbox]);

    const companyName = (values.name ?? "").toString().trim() || null;

    const composerTarget = useMemo<ComposerTarget | null>(() => {
        if (!compose || !companyId) return null;
        return {
            to: [compose.email],
            subject: compose.subject,
            threadId: null,
            // Filed against this record by a person's decision, which ingest
            // never overwrites — the same link the company's Correo tab writes.
            link: { entity_type: "company", entity_id: companyId },
            draftContext: {
                entity_type: "company",
                entity_id: companyId,
                employer_key: employerKey ?? undefined,
                tone: compose.tone
            },
            employerName: companyName,
            recipientName: compose.name
        };
    }, [compose, companyId, employerKey, companyName]);

    const composer = (origin: string) => (compose?.origin === origin && composerTarget ? (
        <EmailComposer
            client={client}
            compact
            canSend={mailbox?.sending !== false}
            target={composerTarget}
            onSent={async () => {
                setCompose(null);
                setSent(n => n + 1);
                snackbar.open({ type: "success", message: "Enviado" });
            }}
            onCancel={() => setCompose(null)}
        />
    ) : null);

    if (!companyId) {
        return (
            <div className="p-6">
                <Typography variant="body1" color="secondary">
                    Guarda la ficha primero. Esta portada resume lo que ya está en la base —
                    sus vacantes y su correo se archivan contra un registro que existe.
                </Typography>
            </div>
        );
    }

    const openJobs = (jobs ?? []).filter(j => j.status !== "closed");
    const location = [values.city, values.country].filter(Boolean).join(", ") || values.location;

    return (
        <div className={coverRootCls}>
            {/* ── Who they are ────────────────────────────────────── */}
            <div className="flex items-start gap-4">
                {values.logo_url && (
                    // Decorative: the name is right beside it. `object-contain`
                    // rather than `cover` — a logo cropped to a circle is a
                    // different mark.
                    <img
                        src={values.logo_url}
                        alt=""
                        className={cls("w-16 h-16 rounded object-contain shrink-0 border bg-white p-1", defaultBorderMixin)}
                    />
                )}
                <div className="flex flex-col gap-2 min-w-0 grow">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Typography variant="h5" component="h2" className="!mb-0">
                            {(values.name ?? "").toString().trim() || "(empresa sin nombre)"}
                        </Typography>
                        {values.is_claimed && <Chip size="small" colorScheme="greenLighter">Reclamada</Chip>}
                        {prospect?.status && (
                            <Chip size="small" colorScheme={STATUS_COLOR[prospect.status as ProspectStatus] ?? "grayLighter"}>
                                {statusLabel(prospect.status)}
                            </Chip>
                        )}
                    </div>

                    {values.tagline && (
                        <Typography variant="body2" color="secondary">{String(values.tagline)}</Typography>
                    )}

                    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                        {values.website && (
                            <a href={values.website} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                                <GlobeIcon size={iconSize.smallest} />
                                {String(values.website).replace(/^https?:\/\//, "")}
                            </a>
                        )}
                        {values.linkedin_url && (
                            <a href={values.linkedin_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                                <LinkIcon size={iconSize.smallest} /> LinkedIn
                            </a>
                        )}
                        {location && (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                                <MapPinIcon size={iconSize.smallest} className="opacity-60" />
                                {location}
                            </span>
                        )}
                    </div>

                    <Typography variant="caption" color="secondary">
                        {[
                            values.industry ? String(values.industry) : null,
                            values.size ? SIZE_LABEL[String(values.size)] ?? String(values.size) : null,
                            values.domain ? String(values.domain) : null,
                            values.created_at ? `en la base ${sinceLabel(values.created_at)}` : null
                        ].filter(Boolean).join(" · ")}
                    </Typography>
                </div>
            </div>

            {/* ── What to do about them ───────────────────────────── */}
            {(() => {
                // The nine real relationships in a directory of two hundred
                // logo holders. A logo holder has no next step and gets no
                // box; a company somebody registered, wrote from or is
                // working does, and it says which of the three it is.
                const real = Boolean(values.is_claimed) || ["signup", "manual"].includes(String(values.source ?? ""))
                    || members.length > 0 || leads.length > 0 || Boolean(prospect);
                if (!real) return null;
                const untakenLead = leads.find(l => !l.employer_key) ?? null;
                const writeTo = members.find(m => (m.contact_email ?? "").trim()) ?? null;
                const writeToName = writeTo ? (writeTo.contact_name ?? "").trim() || null : null;
                let label: string;
                let hint: string;
                // One of two things, never both: write to somebody, here, or
                // go to where this employer is worked, landing on it.
                let write: Omit<ComposeTarget, "origin"> | null = null;
                let href: string | null = null;
                let action: string;
                if (untakenLead) {
                    const asked = (untakenLead.requested_services ?? []).map(k => LEAD_SERVICE_LABEL[k] ?? k).join(", ");
                    label = "Atender";
                    hint = `Se registró como empresa en sustentalent.com ${sinceLabel(untakenLead.created_at)}${asked ? ` marcando ${asked}` : ""}${untakenLead.needs_description ? `: «${String(untakenLead.needs_description).trim()}»` : ""}. Nadie la ha atendido todavía.`;
                    if (writeTo?.contact_email) {
                        write = {
                            email: writeTo.contact_email,
                            name: writeToName,
                            subject: `Re: ${companyName ?? ""} en SustenTalent`,
                            // They asked for something; this is the answer.
                            tone: "respuesta"
                        };
                        action = `Escribir a ${writeToName || writeTo.contact_email}`;
                    } else {
                        href = prospectsUrl({ inbound: untakenLead.id });
                        action = "Verla en «Desde la web»";
                    }
                } else if (prospect) {
                    label = prospect.next_action_at ? dueLabel(prospect.next_action_at) : "En prospección";
                    hint = prospect.next_action
                        ? `Próximo paso: ${prospect.next_action}.`
                        : prospect.last_contacted_at
                            ? `Último contacto ${sinceLabel(prospect.last_contacted_at)}; nada programado.`
                            : "Está en la lista y nadie la ha contactado todavía.";
                    href = prospectsUrl({ employer: employerKey });
                    action = "Trabajarla en «Empresas que contratan»";
                } else if (writeTo) {
                    label = "Cliente con cuenta";
                    hint = `${members.length === 1 ? "Una persona tiene" : `${members.length} personas tienen`} acceso al panel de empresa${applicationsReceived ? `; ${applicationsReceived} ${plural(applicationsReceived, "candidatura recibida", "candidaturas recibidas")} a sus vacantes` : ""}. Nada pendiente, salvo lo que haya en Correo.`;
                    write = { email: writeTo.contact_email as string, name: writeToName, subject: "", tone: "seguimiento" };
                    action = `Escribir a ${writeToName || writeTo.contact_email}`;
                } else {
                    label = "Relación sin contacto";
                    hint = "Es una empresa real de la base, pero no hay nadie a quien escribir: ni tiene cuenta, ni sabemos de nadie que trabaje ahí.";
                    href = prospectsUrl({ employer: employerKey });
                    action = "Buscar a quién escribir";
                }
                const writing = compose?.origin === "next";
                return (
                    <>
                        <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Typography variant="micro" color="secondary">Lo siguiente</Typography>
                                <Chip size="small" colorScheme={untakenLead ? "redLighter" : prospect ? "orangeLighter" : "greenLighter"}>{label}</Chip>
                            </div>
                            <Typography variant="body2">{hint}</Typography>
                            {write ? (
                                <div>
                                    <Button size="small" onClick={() => setCompose(writing ? null : { ...write!, origin: "next" })}>
                                        {writing ? "Cerrar el mensaje" : action}
                                    </Button>
                                </div>
                            ) : href && (
                                // A different screen, so a real navigation —
                                // and to this employer on it, which the
                                // address carries.
                                <div>
                                    <a href={href} className="text-sm text-primary hover:underline">{action} →</a>
                                </div>
                            )}
                        </Card>
                        {composer("next")}
                    </>
                );
            })()}

            {/* ── Who manages it, and who is asking to (drizzle/0062) ── */}
            <CompanyClaimsSection client={client} companyId={companyId} onDecided={load} />

            {/* ── The people ──────────────────────────────────────── */}
            {members.length > 0 && (
                <div className="flex flex-col gap-2">
                    <Typography variant="subtitle2" component="h3">Personas con cuenta ({members.length})</Typography>
                    {members.map(m => (
                        <React.Fragment key={m.id}>
                            <Card className="p-3 flex items-center gap-3 flex-wrap">
                                <div className="grow min-w-0">
                                    <Typography variant="body2" className="font-medium">
                                        {(m.contact_name ?? "").trim() || "(sin nombre)"}
                                        {m.contact_position ? <span className="font-normal opacity-70"> · {m.contact_position}</span> : null}
                                    </Typography>
                                    {m.contact_email && (
                                        // Writes to them here, under their card —
                                        // it used to leave for Correo's composer.
                                        <button
                                            type="button"
                                            className="text-sm text-primary hover:underline text-left"
                                            title={`Escribir a ${m.contact_email}`}
                                            onClick={() => setCompose(compose?.origin === m.id ? null : {
                                                email: m.contact_email as string,
                                                name: (m.contact_name ?? "").trim() || null,
                                                subject: "",
                                                tone: "seguimiento",
                                                origin: m.id
                                            })}
                                        >
                                            {m.contact_email}
                                        </button>
                                    )}
                                </div>
                                <Typography variant="body2" color="secondary">
                                    {MEMBER_ROLE_LABEL[m.role ?? ""] ?? m.role ?? ""}{m.created_at ? ` · desde ${sinceLabel(m.created_at)}` : ""}
                                </Typography>
                            </Card>
                            {composer(m.id)}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {/* ── Who we presented, and what they did with it ─────── */}
            <CompanyPresentations
                companyId={companyId}
                companyName={(values.name ?? "").toString().trim() || "esta empresa"}
            />

            {/* ── Who has been asked to join ─────────────────────── */}
            <CompanyInvitationsPanel companyId={companyId} />

            {/* ── Where the outreach stands ───────────────────────── */}
            {prospect && (
                <Card className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Typography variant="micro" color="secondary">Empresas que contratan</Typography>
                        {prospect.next_action_at && (
                            <Chip size="small" colorScheme={DUE_COLOR[dueState(prospect.next_action_at)]}>
                                {dueLabel(prospect.next_action_at)}
                            </Chip>
                        )}
                    </div>
                    <Typography variant="body2">
                        {[
                            prospect.next_action ? `Próximo paso: ${prospect.next_action}.` : null,
                            prospect.last_contacted_at
                                ? `Último contacto ${sinceLabel(prospect.last_contacted_at)}.`
                                : "Nadie la ha contactado todavía.",
                            contacts ? `${contacts} ${contacts === 1 ? "persona" : "personas"} de contacto.` : null
                        ].filter(Boolean).join(" ")}
                    </Typography>
                    <Typography variant="body2" color="secondary">
                        Se trabaja en{" "}
                        <a href={prospectsUrl({ employer: employerKey })} className="text-primary hover:underline">
                            «Empresas que contratan»
                        </a>
                        , que es donde están sus personas, su historial y el botón de escribir.
                    </Typography>
                </Card>
            )}

            {!prospect && employerKey && (
                <Typography variant="body2" color="secondary">
                    Todavía no está en «Empresas que contratan». Aparecerá ahí en cuanto le
                    veamos publicando una vacante, o si alguien la añade a mano.
                </Typography>
            )}

            <Separator />

            {/* ── Are they hiring ─────────────────────────────────── */}
            <div className="flex flex-col gap-3">
                <Typography variant="subtitle2" component="h3">
                    {jobs === null
                        ? "Vacantes"
                        : `Vacantes (${jobs.length}${openJobs.length !== jobs.length ? ` · ${openJobs.length} abiertas` : ""})`}
                </Typography>
                {jobsFailed && (
                    <Typography variant="body2" color="secondary">
                        No se pudieron leer sus vacantes.
                    </Typography>
                )}
                {jobs?.length === 0 && (
                    <Typography variant="body2" color="secondary">
                        Ninguna vacante en el tablón apunta a esta empresa.
                    </Typography>
                )}
                {jobs?.slice(0, JOBS_SHOWN).map(job => (
                    <Card key={job.id} className="p-3 flex items-center gap-2 flex-wrap">
                        <Typography variant="body2" className="grow font-medium">
                            <EntityLink kind="job" id={job.id}>{(job.title ?? "").trim() || "(vacante sin título)"}</EntityLink>
                        </Typography>
                        <Typography variant="body2" color="secondary">
                            {sinceLabel(job.created_at)}
                        </Typography>
                        <Chip size="small" colorScheme={JOB_STATUS_COLOR[String(job.status)] ?? "grayLighter"}>
                            {JOB_STATUS_LABEL[String(job.status)] ?? String(job.status ?? "")}
                        </Chip>
                    </Card>
                ))}
                {jobs && jobs.length > JOBS_SHOWN && (
                    <Typography variant="body2" color="secondary">
                        Y {jobs.length - JOBS_SHOWN} más, en la pestaña «Vacantes».
                    </Typography>
                )}
            </div>

            <Separator />

            {/* ── What the team wrote down ────────────────────────── */}
            <Notes kind="company" entityId={companyId} />

            <Separator />

            {/* ── Have we spoken ──────────────────────────────────── */}
            <RecentThreads
                kind="company"
                entityId={companyId}
                // Both halves of one business: threads filed against this record
                // and threads filed against its prospect key.
                employerKey={employerKey}
                emptyLabel="Todavía no hemos hablado con esta empresa."
                refresh={sent}
            />

            {(values.description || values.mission) && (
                <>
                    <Separator />
                    <div className="flex flex-col gap-3">
                        <Typography variant="subtitle2" component="h3">Qué son</Typography>
                        {values.description && (
                            <Typography variant="body1" className="whitespace-pre-wrap break-words">
                                {String(values.description)}
                            </Typography>
                        )}
                        {values.mission && (
                            <Fact label="Misión">
                                <span className="whitespace-pre-wrap break-words">{String(values.mission)}</span>
                            </Fact>
                        )}
                        {Array.isArray(values.colors) && values.colors.length > 0 && (
                            // The brand data is what the public board paints
                            // with, so it is worth seeing on the record it is
                            // painted from — as values, not as a colour picker.
                            <Fact label="Marca"><ChipList values={values.colors.map(String)} /></Fact>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default CompanyOverview;
